import { FC, ReactNode, RefObject, useRef, useState } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { MeterGroup } from 'primereact/metergroup';
import { SkillStatesLabel } from '../../models/SkillState';
import { ProgressBar, ProgressBarParent } from '../../models/ProgressBar';
import { Button } from 'primereact/button';
import { TieredMenu } from 'primereact/tieredmenu';
import { MenuItem } from 'primereact/menuitem';
import { Toast } from 'primereact/toast';
import { Skill } from '../../models/Skill';
import SingleUnitSkillDialog from './SingleUnitSkillDialog';
interface Props {
  values: ProgressBar[];
  parent: ProgressBarParent;
  tieredMenuRef?: RefObject<TieredMenu>;
  setItemsTieredMenu?: (i: MenuItem[]) => void;
  fullWidth?: boolean;
}

const SingleUnitProgressBar: FC<Props> = ({ values, parent, tieredMenuRef, setItemsTieredMenu, fullWidth = false }) => {
  const toast = useRef<Toast>(null);
  const host = useRef<HTMLDivElement>(null);
  const pointerAnchor = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState<boolean>(false);
  const [skill, setSkill] = useState<Skill>();

  /**
   * PrimeReact aligns the popup to whichever element it is handed, and flips it to the target's right
   * edge as soon as `target.left + target.width + menu.width` overflows the viewport — note it adds the
   * *target's* width, so a meter spanning the whole bar always trips it and the menu lands at the far
   * end of the bar. Handing it a zero-sized marker parked at the pointer keeps the menu under the click
   * and, as a side effect, stops PrimeReact stretching the menu to the width of the bar.
   */
  const openMenuAtPointer = (event: React.MouseEvent<HTMLSpanElement>) => {
    const anchor = pointerAnchor.current;
    const bounds = host.current?.getBoundingClientRect();

    if (!anchor || !bounds) {
      tieredMenuRef?.current?.toggle(event);
      return;
    }

    anchor.style.left = `${event.clientX - bounds.left}px`;
    anchor.style.top = `${event.clientY - bounds.top}px`;
    // Keeps the synthetic event intact (prototype methods included) while overriding the anchor only.
    tieredMenuRef?.current?.toggle(Object.assign(Object.create(event), { currentTarget: anchor }));
  };
  const meter = (props: ProgressBar): ReactNode => {
    const handleClickTieredMenu = (e: React.MouseEvent<HTMLSpanElement>) => {
      if (parent === ProgressBarParent.PROGRESS_PAGE_STUDENT_TAB) {
        e.preventDefault();
        e.stopPropagation();
        openMenuAtPointer(e);

        const handleCopy = async () => {
          try {
            const skillNamesToString: string | undefined = props?.skills?.map(skill => skill?.name)?.join(', ');
            if (skillNamesToString) {
              await navigator.clipboard.writeText(skillNamesToString);
              toast.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Skills have been copied to clipboard!',
                life: 3000,
              });
            }
          } catch (_error) {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: 'Skills have not been copied to clipboard',
              life: 3000,
            });
          }
        };

        const headerTemplate = (item: MenuItem) => (
          <div className={'flex flex-column align-items-center nc_metergroup-menu-header ' + item.className}>
            <h4>{item.label}</h4>({props.count} {props?.count && props?.count > 1 ? 'skills' : 'skill'})
          </div>
        );

        const copyListTemplate = (item: MenuItem) => (
          <Button
            icon={`${item.icon}`}
            label={`${item.label}`}
            onClick={handleCopy}
            className="nc_metergroup-button"
          ></Button>
        );

        const skillItems: MenuItem[] =
          props?.skills?.map(el => {
            return {
              label: el?.name,
              command: () => {
                setSkill(el);
                setVisible(true);
              },
            };
          }) || [];

        const items: MenuItem[] = [
          {
            label: `${props.label}`,
            className: `${props.className}`,
            template: headerTemplate,
          },
          {
            label: 'Copy list of skill(s)',
            icon: 'pi pi-copy',
            template: copyListTemplate,
          },
        ];
        if (setItemsTieredMenu) setItemsTieredMenu(items.concat(skillItems));
      }
      if (parent === ProgressBarParent.SINGLE_UNIT) {
        e.preventDefault();
        e.stopPropagation();
        openMenuAtPointer(e);

        const sortedUsers = [...(props?.users || [])].sort(
          (a, b) =>
            (a.lastName || '').localeCompare(b.lastName || '', undefined, { sensitivity: 'base' }) ||
            (a.firstName || '').localeCompare(b.firstName || '', undefined, { sensitivity: 'base' }),
        );

        const handleCopy = async () => {
          try {
            const userNamestoString: string | undefined = sortedUsers
              .map(user => `${user.firstName} ${user.lastName}`)
              .join(', ');
            if (userNamestoString) {
              await navigator.clipboard.writeText(userNamestoString);
              toast.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Users have been copied to clipboard!',
                life: 3000,
              });
            }
          } catch (_error) {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: 'Users have not been copied to clipboard',
              life: 3000,
            });
          }
        };

        const headerTemplate = (item: MenuItem) => (
          <div className={'flex flex-column align-items-center nc_metergroup-menu-header ' + item.className}>
            <h4>{item.label}</h4>({item.target} students)
          </div>
        );

        const copyListTemplate = (item: MenuItem) => (
          <Button
            icon={`${item.icon}`}
            label={`${item.label}`}
            onClick={handleCopy}
            className="nc_metergroup-button"
          ></Button>
        );

        const userItems: MenuItem[] =
          sortedUsers.map(el => {
            return {
              label: el.firstName + ' ' + el.lastName,
              // This label will end up in an aria-label attribute so we have to
              // hide the student elements
              className: 'ph-no-capture',
              // items: [
              //   {
              //     label: 'Placeholder for timeline',
              //   },
              // ],
            };
          }) || [];

        const items: MenuItem[] = [
          {
            label: `${props.label}`,
            target: `${props?.valuePerType?.value}`,
            className: `${props.className}`,
            template: headerTemplate,
          },
          {
            label: 'Copy list of student(s)',
            icon: 'nc_icon-nc_copy icon-sm',
            template: copyListTemplate,
          },
        ];
        if (setItemsTieredMenu) setItemsTieredMenu(items.concat(userItems));
      } else {
        return;
      }
    };

    return (
      <>
        {props.value > 0 && <Tooltip target={`.${props.className}`} mouseTrack position="top" />}
        <span
          key={props.label}
          className={`${props.className} bar-md`}
          onClick={handleClickTieredMenu}
          data-pr-tooltip={
            parent === ProgressBarParent.PROGRESS_PAGE_STUDENT_TAB
              ? `${props.label}: ${props.count} \n \n`
              : parent === ProgressBarParent.SINGLE_UNIT
              ? `${props.label} - ${props.valuePerType?.value} Students`
              : `${props.label}: ${props.value}% \n \n` +
                `Unit Skills: ${props.valuePerType?.Unit} \n` +
                `Support Skills: ${props.valuePerType?.Support}`
          }
          style={{
            width: props.value + '%',
            borderRight: props.value === 0 || props.label === SkillStatesLabel.NOT_STARTED ? '' : '2px solid white',
          }}
        />
      </>
    );
  };

  return (
    <div ref={host} className="nc_metergroup-host">
      <Toast ref={toast} position="top-right" appendTo={document.body} />
      <span ref={pointerAnchor} className="nc_metergroup-pointer-anchor" aria-hidden="true" />
      <MeterGroup
        max={70}
        min={0}
        className={`nc_metergroup ${fullWidth ? 'nc_metergroup-wide' : 'nc_metergroup-fixed-width'}`}
        values={values}
        meter={meter as () => ReactNode}
      />
      {visible && skill && <SingleUnitSkillDialog visible={visible} setVisible={setVisible} skill={skill} />}
    </div>
  );
};

export default SingleUnitProgressBar;
